import {
    ApolloClient,
    createHttpLink,
    DocumentNode,
    gql,
    InMemoryCache,
    TypedDocumentNode,
    useQuery
} from '@apollo/client';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { Contact, Note, Order, Scalars, Workflow, WorkflowStepGroup } from 'sr-types/lib/order/v1/graphql';
import { AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import { authLink } from '../../../common/auth/api';
import { UserContext } from '../../../common/auth/UserContext';
import { AddressFragment } from '../../../common/components/location';
import { FETCH_POLICY_CONFIG } from '../../../common/constants/constants';
import {
    CostSummaryFragment,
    DateRangeFragment,
    FieldFragment,
    ReferenceFragment
} from '../../../common/list/fragments';
import { ORDER_ROLES, ORDER_STATE } from '../../../orders/ordersConstants';

export const orderClient = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/order/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

export type OrderType = {
    order: Order;
    workflow: Workflow & { requestApproval: boolean };
    note: Note;
    attachments: AttachmentInput[]; // Pending attachments that are yet to be saved
};

export const getIndexOfRoles = (workflow: Workflow, role: string) => {
    const index = workflow.workflowStepGroups.findIndex((workflowStepGroup) => workflowStepGroup?.role === role);
    return index;
};

export const isRequestApprovalEnabled = (workflow: Workflow): boolean => {
    const approverIndex = getIndexOfRoles(workflow, ORDER_ROLES.approver);
    return Boolean(
        workflow?.workflowStepGroups[approverIndex]?.workflowSteps[0]?.workflowParticipant?.contacts?.length
    );
};

export const orderToState = (order: Order) => {
    const { production, orderOrganization, salesOrganization } = order;
    let updatedOrderOrganization = {};
    const hasOrderOrganizationContact = !isEmpty(orderOrganization?.contact);
    const hasOrderOrganizationAddress = Object.values(orderOrganization?.address || {}).some(
        (value) => !isEmpty(value)
    );

    //If orderOrganization is available set includeOrderOrganization switch = true
    if (orderOrganization?.name || hasOrderOrganizationContact || hasOrderOrganizationAddress) {
        const orderOrgLocation = { address: orderOrganization?.address, label: orderOrganization?.address?.label };
        updatedOrderOrganization = {
            ...orderOrganization,
            includeOrderOrganization: true,
            contact: orderOrganization?.contact ? [orderOrganization.contact] : [],
            location: orderOrgLocation,
            address: undefined
        };
    }
    //If production is available set includeProduction switch = true
    let updatedProduction = {};
    if (production?.name) {
        const productionLocation = { address: production?.address, label: production?.address?.label };
        updatedProduction = {
            ...production,
            includeProduction: true,
            contact: production?.contact ? [production.contact] : [],
            location: productionLocation,
            address: undefined
        };
    }

    const salesOrgLocation = { address: salesOrganization?.address, label: salesOrganization?.address?.label };
    let updatedSalesOrganization = {
        ...salesOrganization,
        contact: salesOrganization?.contact ? [salesOrganization.contact] : [],
        location: salesOrgLocation,
        address: undefined
    };
    const updatedOrder = {
        ...order,
        orderOrganization: updatedOrderOrganization,
        production: updatedProduction,
        salesOrganization: updatedSalesOrganization
    };
    return updatedOrder;
};

const assignEmptyWorkflowSteps = (workflow: Workflow, role: string, workflowStepGroups: WorkflowStepGroup[]) => {
    const index = workflow?.workflowStepGroups.findIndex((workflowStepGroup) => workflowStepGroup?.role === role);
    if (index === -1) {
        workflow.workflowStepGroups[workflow?.workflowStepGroups?.length] = workflowStepGroups.find(
            (workflowStepGroup) => workflowStepGroup?.role === role
        );
    }
};

export const workflowToState = (order: Order, workflow: Workflow) => {
    let updatedWorkflow = {};
    if (isNil(workflow)) {
        // Assign empty workflowStepGroups
        updatedWorkflow['workflowStepGroups'] =
            order.state === ORDER_STATE.order
                ? cloneDeep(EMPTY_WORKFLOWSTEPGROUPS)
                : cloneDeep(EMPTY_QOUTE_WORKFLOWSTEPGROUPS);
    } else {
        // Only assign empty workflowStepGroup for the role not available
        updatedWorkflow = { ...cloneDeep(workflow) };
        if (order.state === ORDER_STATE.order) {
            const rolesInOrder = [...Object.values(ORDER_ROLES)];
            rolesInOrder.forEach((role) => assignEmptyWorkflowSteps(updatedWorkflow, role, EMPTY_WORKFLOWSTEPGROUPS));
        } else {
            const rolesInQuote = [ORDER_ROLES.approver, ORDER_ROLES.carbonCopy];
            rolesInQuote.forEach((role) =>
                assignEmptyWorkflowSteps(updatedWorkflow, role, EMPTY_QOUTE_WORKFLOWSTEPGROUPS)
            );
        }
    }
    // Enable request toggle when approver is available
    updatedWorkflow['requestApproval'] = isRequestApprovalEnabled(updatedWorkflow);
    return updatedWorkflow;
};

export const stateToOrder = (order): Order => {
    const updatedOrder = { ...cloneDeep(order) };
    if (updatedOrder.production?.location && updatedOrder.production.location.address) {
        updatedOrder.production.address = updatedOrder.production.location.address;
    }
    if (updatedOrder.production?.productionReference) {
        updatedOrder.production.name = updatedOrder.production.productionReference.label;
    }
    if (updatedOrder.orderOrganization?.location && updatedOrder.orderOrganization.location.address) {
        updatedOrder.orderOrganization.address = updatedOrder.orderOrganization.location.address;
    }
    if (updatedOrder.orderOrganization?.contact) {
        updatedOrder.orderOrganization.contact = updatedOrder.orderOrganization.contact[0];
    }
    if (updatedOrder.orderOrganization?.organizationReference) {
        updatedOrder.orderOrganization.name = updatedOrder.orderOrganization.organizationReference.label;
    }
    if (updatedOrder.production?.contact) {
        updatedOrder.production.contact = updatedOrder.production.contact[0];
    }
    if (updatedOrder.salesOrganization?.organizationReference) {
        updatedOrder.salesOrganization.name = updatedOrder.salesOrganization.organizationReference.label;
    }
    if (updatedOrder.salesOrganization?.contact) {
        updatedOrder.salesOrganization.contact = updatedOrder.salesOrganization.contact[0];
    }
    if (!updatedOrder.production.includeProduction) {
        updatedOrder.production.productionReference = null;
        updatedOrder.production.contact = {};
        updatedOrder.production.address = {};
    }
    if (!updatedOrder.orderOrganization.includeOrderOrganization) {
        updatedOrder.orderOrganization.organizationReference = null;
        updatedOrder.orderOrganization.contact = {};
        updatedOrder.orderOrganization.address = {};
    }
    if (updatedOrder.salesOrganization?.location && updatedOrder.salesOrganization.location.address) {
        updatedOrder.salesOrganization.address = updatedOrder.salesOrganization.location.address;
    }
    delete updatedOrder.orderOrganization.location;
    delete updatedOrder.production.location;
    delete updatedOrder.orderOrganization.includeOrderOrganization;
    delete updatedOrder.production.includeProduction;
    delete updatedOrder.salesOrganization.location;
    delete updatedOrder.reference;
    delete updatedOrder.internalNote;
    delete updatedOrder.security;
    delete updatedOrder.production?.address?.label;
    delete updatedOrder.orderOrganization?.address?.label;
    delete updatedOrder.salesOrganization?.address?.label;
    delete updatedOrder.isQuote;
    return updatedOrder;
};

const removeWorkflowStepGroup = (updatedWorkflow: Workflow, role: string) => {
    const roleIndex = getIndexOfRoles(updatedWorkflow, role);
    roleIndex > -1 && updatedWorkflow.workflowStepGroups.splice(roleIndex, 1);
};

export const stateToWorkflow = (workflow): Workflow => {
    const updatedWorkflow = { ...cloneDeep(workflow) };
    if (updatedWorkflow?.requestApproval) {
        const approvers = updatedWorkflow.workflowStepGroups.find(
            (workflowStepGroup) => workflowStepGroup.role === ORDER_ROLES.approver
        )?.workflowSteps[0].workflowParticipant.contacts;

        approvers.forEach((approver) => delete approver.id);
    } else {
        // Delete workflowStepGroup for role Approver when contact not available
        removeWorkflowStepGroup(updatedWorkflow, ORDER_ROLES.approver);
    }

    const signers = updatedWorkflow.workflowStepGroups.find(
        (workflowStepGroup) => workflowStepGroup?.role === ORDER_ROLES.signer
    )?.workflowSteps[0].workflowParticipant.contacts;

    if (signers?.length) {
        signers.forEach((signer) => delete signer.id);
    } else {
        // Delete workflowStepGroup for role signer when contact not available
        removeWorkflowStepGroup(updatedWorkflow, ORDER_ROLES.signer);
    }

    const carbonCopies = updatedWorkflow.workflowStepGroups.find(
        (workflowStepGroup) => workflowStepGroup?.role === ORDER_ROLES.carbonCopy
    )?.workflowSteps[0].workflowParticipant.contacts;

    if (carbonCopies?.length) {
        carbonCopies.forEach((notify) => delete notify.id);
    } else {
        // Delete workflowStepGroup for role carboncopy when contact not available
        removeWorkflowStepGroup(updatedWorkflow, ORDER_ROLES.carbonCopy);
    }

    delete updatedWorkflow?.requestApproval;

    return updatedWorkflow;
};



export const EMPTY_WORKFLOWSTEPGROUPS: WorkflowStepGroup[] = [
    {
        role: 'Approver',
        isSequential: false,
        workflowSteps: [
            {
                workflowParticipant: {
                    contacts: []
                }
            }
        ]
    },
    {
        role: 'Signer',
        isSequential: false,
        workflowSteps: [
            {
                workflowParticipant: {
                    contacts: []
                }
            }
        ]
    },
    {
        role: 'CarbonCopy',
        workflowSteps: [
            {
                workflowParticipant: {
                    contacts: []
                }
            }
        ]
    }
];

export const EMPTY_QOUTE_WORKFLOWSTEPGROUPS: WorkflowStepGroup[] = [
    {
        role: 'Approver',
        isSequential: false,
        workflowSteps: [
            {
                workflowParticipant: {
                    contacts: []
                }
            }
        ]
    },
    {
        role: 'CarbonCopy',
        provider: '',
        messageContent: '',
        workflowSteps: [
            {
                workflowParticipant: {
                    contacts: []
                }
            }
        ]
    }
];

export const ContactFragment: TypedDocumentNode<Contact> = gql`
    fragment Contact on Contact {
        name {
            firstName
            lastName
            salutation
            suffix
        }
        isPrimary
        contactInfo {
            email {
                address
                typeString
                verified
            }
            phone {
                number
                typeString
            }
        }
        personReference {
            ...Reference
        }
        role
        organization
        organizationReference {
            ...Reference
        }
        label
        production
        productionRole
        title
        organizationDepartment
        productionDepartment
    }
    ${ReferenceFragment}
`;

const OrderDetailsFragment: TypedDocumentNode<Order> = gql`
    fragment OrderDetails on Order {
        identity {
            id
            preSave
        }
        name
        status
        state
        orderNumber
        orderVersion
        currency
        opportunityReference {
            ...Reference
        }
        bookingPeriod {
            ...DateRange
        }
        attachments {
            ...Reference
        }
        attributes {
            ...Field
        }
        associatedFieldDefinitionReferences {
            ...Reference
        }
        internalNote {
            identity {
                id
            }
            reference {
                ...Reference
            }
            title
            content
            entityReference {
                ...Reference
            }
        }
        isQuote
        orderOrganization {
            name
            organizationReference {
                ...Reference
            }
            address {
                ...AddressFields
                label
            }
            contact {
                ...Contact
            }
        }
        salesOrganization {
            name
            organizationReference {
                ...Reference
            }
            address {
                ...AddressFields
                label
            }
            contact {
                ...Contact
            }
        }
        production {
            name
            productionReference {
                ...Reference
            }
            address {
                ...AddressFields
                label
            }
            contact {
                ...Contact
            }
        }
        reference {
            ...Reference
        }
        isExternalOMS
        customerPO
        terms
        costSummary {
            ...CostSummary
        }
        security {
            allowedActions
        }
    }
    ${ReferenceFragment}
    ${FieldFragment}
    ${AddressFragment}
    ${ContactFragment}
    ${CostSummaryFragment}
    ${DateRangeFragment}
`;

export const WorkflowFragment: TypedDocumentNode<Workflow> = gql`
    fragment Workflow on Workflow {
        identity {
            id
        }
        status
        context {
            entityReference {
                ...Reference
            }
            documentReferences {
                ...Reference
            }
        }
        workflowStepGroups {
            role
            alternateId {
                label
                value
                url
            }
            provider
            messageContent
            isSequential
            workflowSteps {
                workflowParticipant {
                    contacts {
                        ...Contact
                    }
                    alternateId {
                        label
                        value
                        url
                    }
                }
            }
        }
        receivedEvents
    }
    ${ReferenceFragment}
    ${ContactFragment}
`;

export const draftOrderFromBookingQuery: DocumentNode = gql`
    query DraftOrderFromBooking($bookingId: ID!, $orderId: ID) {
        draftOrderFromBooking(bookingId: $bookingId, orderId: $orderId) {
            order {
                ...OrderDetails
            }
            workflow {
                ...Workflow
            }
        }
    }
    ${OrderDetailsFragment}
    ${WorkflowFragment}
`;

export const draftQuoteFromBookingQuery: DocumentNode = gql`
    query DraftQuoteFromBooking($bookingId: ID!, $orderId: ID) {
        draftQuoteFromBooking(bookingId: $bookingId, orderId: $orderId) {
            order {
                ...OrderDetails
            }
            workflow {
                ...Workflow
            }
        }
    }
    ${OrderDetailsFragment}
    ${WorkflowFragment}
`;

export const OrderSummaryForBookingQuery: DocumentNode = gql`
    query OrderSummaryForBooking($bookingId: ID!) {
        orderSummaryForBooking(bookingId: $bookingId) {
            bookingId
            canCreateOrder
            canCreateRevision
            canCreateQuote
            orderStatus
            orders {
                identity {
                    id
                }
                name
                status
                orderNumber
                orderVersion
                orderDate
                needsRevision
                costSummary {
                    base {
                        currencyCode
                        units
                        nanos
                    }
                    discount {
                        currencyCode
                        units
                        nanos
                    }
                    discountedTotal {
                        currencyCode
                        units
                        nanos
                    }
                    taxes {
                        currencyCode
                        units
                        nanos
                    }
                    total {
                        currencyCode
                        units
                        nanos
                    }
                }
            }
            hasConfirmedOrderOrQuote
        }
    }
`;

export const orderQuery: DocumentNode = gql`
    query Order($id: ID!) {
        order(id: $id) {
            order {
                ...OrderDetails
                orderDocumentReference {
                    ...Reference
                }
            }
            workflow {
                ...Workflow
            }
        }
    }
    ${OrderDetailsFragment}
    ${WorkflowFragment}
    ${ReferenceFragment}
`;

export const saveOrderQuery: DocumentNode = gql`
    mutation SaveOrder($orderInput: OrderInput, $workflowInput: WorkflowInput, $noteInput: NoteInput) {
        saveOrder(orderInput: $orderInput, workflowInput: $workflowInput, noteInput: $noteInput) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const saveQuoteQuery: DocumentNode = gql`
    mutation SaveQuote($orderInput: OrderInput, $workflowInput: WorkflowInput, $noteInput: NoteInput) {
        saveQuote(orderInput: $orderInput, workflowInput: $workflowInput, noteInput: $noteInput) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const confirmOrderQuery: DocumentNode = gql`
    mutation ConfirmOrder($orderInput: OrderInput, $workflowInput: WorkflowInput, $noteInput: NoteInput) {
        confirmOrder(orderInput: $orderInput, workflowInput: $workflowInput, noteInput: $noteInput) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const sendQuoteQuery: DocumentNode = gql`
    mutation SendQuote($orderInput: OrderInput, $workflowInput: WorkflowInput, $noteInput: NoteInput) {
        sendQuote(orderInput: $orderInput, workflowInput: $workflowInput, noteInput: $noteInput) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const cancelOrderQuery: DocumentNode = gql`
    mutation CancelOrder($input: CancelOrderInput) {
        cancelOrder(input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const previewOrderQuery: DocumentNode = gql`
    mutation PreviewOrder($orderInput: OrderInput, $workflowInput: WorkflowInput, $noteInput: NoteInput) {
        previewOrder(orderInput: $orderInput, workflowInput: $workflowInput, noteInput: $noteInput) {
            documentReference {
                ...Reference
            }
            workflowReference {
                ...Reference
            }
            orderReference {
                ...Reference
            }
            errors {
                field
                message
            }
        }
    }
    ${ReferenceFragment}
`;

export const previewQuoteQuery: DocumentNode = gql`
    mutation PreviewQuote($orderInput: OrderInput, $workflowInput: WorkflowInput, $noteInput: NoteInput) {
        previewQuote(orderInput: $orderInput, workflowInput: $workflowInput, noteInput: $noteInput) {
            documentReference {
                ...Reference
            }
            workflowReference {
                ...Reference
            }
            orderReference {
                ...Reference
            }
            errors {
                field
                message
            }
        }
    }
    ${ReferenceFragment}
`;

export const useOrderSummaryForBookingData = (bookingId: string) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const {
        loading: orderSummaryForBookingLoading,
        error: orderSummaryForBookingError,
        refetch: orderSummaryForBookingRefetch,
        data: orderSummaryForBookingData
    } = useQuery(OrderSummaryForBookingQuery, {
        client: orderClient,
        variables: { bookingId: bookingId },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        skip: !bookingId
    });

    return { orderSummaryForBookingLoading, orderSummaryForBookingData, orderSummaryForBookingRefetch };
};

export const getPreSaveOrderId: DocumentNode = gql`
    query getPreSaveOrderId {
        getPreSaveOrderId
    }
`;

export const useGetPreSaveOrderId = () => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const { data, loading, refetch, error } = useQuery<{ getPreSaveOrderId: Scalars['ID'] }>(getPreSaveOrderId, {
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        client: orderClient,
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: true
    });
    return { data, loading, refetch, error };
};
